import React, { useContext, useEffect, useState } from 'react'
import {
  Experiment,
  ExperimentInput,
  ExperimentInventoryInput,
  ExperimentType,
  SelectableExperimentationGroup,
} from '../../Api/experiments'
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  ITag,
  Link,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
} from 'office-ui-fabric-react'
import { NumberInput } from '../../Components/Input/NumberInput'
import { SimpleDateInput } from '../../Components/Input/SimpleDateInput'
import moment from 'moment'
import { TagInput } from '../../Components/Input/TagInput'
import { Account } from '../../Api/integrations'
import { CreateOrManageExperimentParameters } from './CreateOrManageExperimentParameters'
import InfoBox from '../CampaignCollectionScreen/CampaignDialog/InfoBox'
import { SessionContext } from '../../Contexts/SessionContext'

interface Error {
  field: string
  message: string
}

interface InventoryTag extends ITag {
  account?: string
  domain?: string
  experimentationGroupId?: string
}

export function CreateOrManageExperimentDialog({
  experimentTypes,
  accounts,
  experimentationGroups,
  isNewExperiment,
  experiment,
  disabled,
  error,
  onCreate,
  onSetEndDate,
  onCancel,
}: {
  experimentTypes: ExperimentType[]
  accounts: Account[]
  experimentationGroups: SelectableExperimentationGroup[]
  isNewExperiment: boolean
  experiment?: Experiment
  disabled: boolean
  error: string
  onCreate: (experiment: ExperimentInput) => void
  onSetEndDate: (_: { id: string; endDate: string }) => void
  onCancel: () => void
}) {
  const [errors, setErrors] = useState<Error[]>([])
  const [editExperiment, setEditExperiment] = useState<ExperimentInput | null>(
    experiment || null
  )
  const [inventoryTags, setInventoryTags] = useState<InventoryTag[]>([])
  useEffect(() => {
    if (!experimentTypes) {
      return
    }
    const defaultExperiment = getDefaultExperiment(experimentTypes[0])
    setEditExperiment((editExperiment) => {
      const baseExperiment = editExperiment || defaultExperiment
      return {
        ...baseExperiment,
        inventory: {
          account: baseExperiment.inventory.account,
          domain: baseExperiment.inventory.domain,
          experimentGroupId:
            experiment && experiment.inventory.experimentGroup
              ? experiment.inventory.experimentGroup.id
              : baseExperiment.inventory.experimentGroupId,
        },
      }
    })
  }, [experimentTypes, experiment])
  useEffect(() => {
    if (!accounts || !experimentationGroups) {
      return
    }
    const accountsAndDomainsTags = accounts.flatMap(
      ({ account, displayName, domains }) => {
        const accountTag = {
          key: account,
          name: displayName,
          account,
        }
        const domainTags = domains.map((domain) => ({
          account,
          domain,
          key: `${account}/${domain}`,
          name: `${displayName}/${domain}`,
        }))
        return [accountTag].concat(domainTags)
      }
    )
    const experimentationGroupsTags = experimentationGroups.map(
      (experimentationGroup) => ({
        key: experimentationGroup.id,
        name: experimentationGroup.title,
        experimentationGroupId: experimentationGroup.id,
      })
    )
    const inventoryTags = [
      ...accountsAndDomainsTags,
      ...experimentationGroupsTags,
    ]
    inventoryTags.sort(sortTagsAlphabetically)
    setInventoryTags(inventoryTags)
  }, [setInventoryTags, accounts, experimentationGroups])
  const sessionContext = useContext(SessionContext)
  if (!accounts || !editExperiment) {
    return null
  }
  const experimentType = experimentTypes.find(
    (experimentType) => experimentType.type === editExperiment.type
  )
  if (!experimentType) {
    return null
  }
  const isFinished =
    !!experiment && !!experiment.endDate && moment().isAfter(experiment.endDate)
  const selectedInventoryTags = inventoryTags.filter(
    (tag) => tag.key === inventoryToTagKey(editExperiment.inventory)
  )
  if (isNewExperiment) {
    experimentTypes = experimentTypes.filter(({ deprecated }) => !deprecated)
  }
  return (
    <Dialog
      hidden={false}
      modalProps={{
        isDarkOverlay: true,
        isBlocking: true,
      }}
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title:
          !isNewExperiment && !!experiment
            ? `Manage ${experiment.type}`
            : 'Create Experiment',
      }}
      minWidth={400}
    >
      <Stack gap={16}>
        <Dropdown
          disabled={disabled || !isNewExperiment}
          required
          label="Experimental Feature"
          errorMessage={isNewExperiment ? getErrorMessage(errors, 'type') : ''}
          selectedKey={editExperiment.type}
          onChange={(_, value?) => {
            if (!value) {
              return
            }
            const type = value.key as Experiment['type']
            const experimentType = experimentTypes.find(
              (experimentType) => experimentType.type === type
            )
            const defaultExperiment = getDefaultExperiment(experimentType!)
            setEditExperiment({ ...editExperiment, ...defaultExperiment, type })
          }}
          options={experimentTypes.map((experimentType) => ({
            key: experimentType.type,
            text: `${experimentType.label} (${experimentType.type})`,
          }))}
          placeholder="Select an option"
        />
        <CreateOrManageExperimentParameters
          disabled={disabled || !isNewExperiment}
          errors={errors}
          parametersDefinition={experimentType.parameters}
          parameters={editExperiment.parameters}
          onChange={(parameters) =>
            setEditExperiment({ ...editExperiment, parameters })
          }
        />
        <TagInput
          disabled={disabled || !isNewExperiment}
          required
          label="Inventory"
          errorMessage={
            isNewExperiment ? getErrorMessage(errors, 'inventory') : ''
          }
          itemLimit={1}
          selectedItems={selectedInventoryTags}
          onResolveSuggestions={(filter) =>
            inventoryTags.filter((tag) =>
              tag.name.toLowerCase().includes(filter.toLowerCase())
            )
          }
          pickerLabels={{
            noResults: 'No account, domain or group matches',
            suggestionsHeader: 'Accounts, domains and groups',
          }}
          onChange={(tags = []) => {
            const inventoryTag = tags.length
              ? inventoryTags.find((tag) => tag.key === tags[0].key) || {}
              : { account: '', domain: '', experimentationGroupId: '' }
            const inventory = {
              account: inventoryTag.account,
              domain: inventoryTag.domain,
              experimentGroupId: inventoryTag.experimentationGroupId,
            }
            setEditExperiment({
              ...editExperiment,
              inventory,
            })
          }}
        />
        {editExperiment.inventory.experimentGroupId ? (
          <div style={{ marginTop: 28 }}>
            <InfoBox type={MessageBarType.info}>
              Experimentation groups are not stored as references, but copies of
              the group at the time of creation of the experiment. The affected
              sites don’t change after the experiment is created. Click
              <Link
                href={`${process.env.PUBLIC_URL}/#/${sessionContext.ctok}/experimentation-groups/#/manage/${editExperiment.inventory.experimentGroupId}`}
                target="_blank"
              >
                here
              </Link>{' '}
              to review affected inventory.
            </InfoBox>
          </div>
        ) : null}
        <div
          style={{
            flexGrow: 1,
            width: '50%',
          }}
        >
          <NumberInput
            disabled={disabled || !isNewExperiment}
            required
            label="Allocation"
            errorMessage={
              isNewExperiment ? getErrorMessage(errors, 'allocation') : ''
            }
            value={editExperiment.allocation}
            min={0}
            max={100}
            onChange={(allocation) => {
              setEditExperiment({
                ...editExperiment,
                allocation: allocation == null ? 100 : allocation,
              })
            }}
          />
        </div>
        <Stack horizontal gap={16}>
          <div
            style={{
              flexGrow: 1,
              width: 'calc(50% - 8px)',
            }}
          >
            <SimpleDateInput
              label="Start Date"
              required
              disabled={disabled || !isNewExperiment}
              errorMessage={
                isNewExperiment ? getErrorMessage(errors, 'startDate') : ''
              }
              value={editExperiment.startDate}
              onChange={(startDate?) => {
                if (!startDate) {
                  return
                }
                setEditExperiment({
                  ...editExperiment,
                  startDate: startDate.toISOString(),
                })
              }}
              storeAs="day-start"
              min={isNewExperiment ? new Date() : undefined}
              format="DD/MM/YYYY"
            />
          </div>
          <div
            style={{
              flexGrow: 1,
              width: 'calc(50% - 8px)',
            }}
          >
            <SimpleDateInput
              label="End Date"
              disabled={disabled || isFinished}
              errorMessage={
                isFinished ? '' : getErrorMessage(errors, 'endDate')
              }
              value={editExperiment.endDate}
              onChange={(endDate?) => {
                setEditExperiment({
                  ...editExperiment,
                  endDate: endDate ? endDate.toISOString() : undefined,
                })
              }}
              storeAs="day-start"
              min={
                isFinished
                  ? undefined
                  : moment
                      .max(moment(), moment(editExperiment.startDate))
                      .toDate()
              }
              format="DD/MM/YYYY"
            />
          </div>
        </Stack>
      </Stack>
      <Separator />
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          styles={{ root: { marginTop: 15 } }}
        >
          {error}
        </MessageBar>
      )}
      <DialogFooter>
        <DefaultButton onClick={onCancel} disabled={disabled} text="Cancel" />
        {isFinished ? null : (
          <PrimaryButton
            onClick={() => {
              if (isNewExperiment) {
                const errors = validate({
                  experiment: editExperiment,
                  experimentType,
                })
                setErrors(errors)
                if (errors.length) {
                  return
                }
                const normalizedExperiment = normalize(editExperiment)
                onCreate(normalizedExperiment)
              } else {
                const errors = editExperiment.endDate
                  ? validateEndDate({
                      startDate: editExperiment.startDate,
                      endDate: editExperiment.endDate || '',
                    })
                  : []
                setErrors(errors)
                if (errors.length) {
                  return
                }
                onSetEndDate({
                  id: experiment!.id,
                  endDate: editExperiment.endDate!,
                })
              }
            }}
            disabled={disabled}
            text={isNewExperiment ? 'Create' : 'Save'}
          />
        )}
      </DialogFooter>
    </Dialog>
  )
}

function validate({
  experiment,
  experimentType,
}: {
  experiment: ExperimentInput
  experimentType: ExperimentType
}): Error[] {
  const errors: Error[] = experiment.endDate
    ? validateEndDate({
        startDate: experiment.startDate,
        endDate: experiment.endDate,
      })
    : []
  if (experiment.allocation < 0 || experiment.allocation > 100) {
    errors.push({
      field: 'allocation',
      message: 'allocation must be between 0 and 100%',
    })
  }
  if (
    !experiment.inventory.account &&
    !experiment.inventory.experimentGroupId
  ) {
    errors.push({
      field: 'inventory',
      message: 'inventory is required',
    })
  }
  if (
    !experiment.startDate ||
    moment(experiment.startDate).isBefore(moment().startOf('day'))
  ) {
    errors.push({
      field: 'startDate',
      message: 'start date must be today or in the future',
    })
  }
  experimentType.parameters.forEach((parameter) => {
    const experimentParameter = experiment.parameters.find(
      (p) => p.name === parameter.name
    )
    if (parameter.required && !experimentParameter) {
      errors.push({
        field: parameter.name,
        message: `${parameter.label} is required`,
      })
    }
  })
  return errors
}

function validateEndDate({
  startDate,
  endDate,
}: {
  startDate: string
  endDate: string
}): Error[] {
  const errors: Error[] = []
  const parsedStartDate = new Date(startDate)
  const parsedEndDate = new Date(endDate)
  if (isNaN(parsedEndDate.getTime())) {
    errors.push({ field: 'endDate', message: 'end date is not a valid date' })
  } else if (parsedEndDate < moment().subtract(1, 'day').toDate()) {
    errors.push({ field: 'endDate', message: 'end date must be in the future' })
  } else if (
    !isNaN(parsedStartDate.getTime()) &&
    parsedEndDate < parsedStartDate
  ) {
    errors.push({
      field: 'endDate',
      message: 'end date must be after start date',
    })
  }
  return errors
}

function normalize(experiment: ExperimentInput): ExperimentInput {
  const isStartDateToday = moment(experiment.startDate).isSame(moment(), 'day')
  return {
    ...experiment,
    startDate: isStartDateToday
      ? new Date().toISOString()
      : experiment.startDate,
  }
}

function getErrorMessage(errors: Error[], field: string) {
  const isSameField = (error: Error) => error.field === field
  const error = errors.find(isSameField)
  return error ? error.message : ''
}

function inventoryToTagKey({
  domain,
  account,
  experimentGroupId,
}: ExperimentInventoryInput): string | undefined {
  if (experimentGroupId) {
    return experimentGroupId
  }
  return domain ? `${account}/${domain}` : account
}

function getDefaultExperiment(experimentType: ExperimentType): ExperimentInput {
  return {
    type: experimentType.type,
    inventory: { account: '', domain: '' },
    allocation: 100,
    startDate: new Date().toISOString(),
    parameters: experimentType.parameters.map((parameter) => {
      switch (parameter.type) {
        case 'choice': {
          return {
            name: parameter.name,
            value: parameter.choices[0].value,
          }
        }
        case 'boolean': {
          return {
            name: parameter.name,
            value: false,
          }
        }
        case 'number': {
          return {
            name: parameter.name,
            value: 0,
          }
        }
        default: {
          return {
            name: parameter.name,
            value: '',
          }
        }
      }
    }),
  }
}

function sortTagsAlphabetically(a: ITag, b: ITag) {
  return a.name.localeCompare(b.name)
}
